import { useMemo, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Filter, Calendar, FileText, MapPin, Layers, UserCheck, RotateCcw, 
  Search, FolderOpen, SlidersHorizontal, ChevronLeft, ChevronRight, Loader2 
} from 'lucide-react';

import type { SubmissionStatus } from '../types/MainType.type';
import { searchDocuments, type DocumentSearchResult } from '../services/DocumentService';
import { useSettings } from '../context/SettingContext';
import { StatusBadge } from '../hooks/StatusBatch';
import type { User } from '../types/user';
import { deleteForm } from '../services/genericFormServices';
import { FormDatePicker } from '../components/Form/FormDatePicker';
import { formatDateDisplay } from '../utils/GlobalUtils';

// Helper: Convert ASCII digits (123) to Burmese digits (၁၂၃)
const toBurmeseDigits = (str: string | number): string => {
  const burmeseNums = ['၀', '၁', '၂', '၃', '၄', '၅', '၆', '၇', '၈', '၉'];
  return String(str).replace(/[0-9]/g, (match) => burmeseNums[parseInt(match, 10)]);
};

// Registered Form Types in Prisma Schema
const FORM_TYPES = [
  { id: 'PLO_FORM_ONE', name: 'PLO ပုံစံ (၁)' },
  { id: 'PLO_FORM_TWO', name: 'PLO ပုံစံ (၂)' },
  { id: 'PLO_FORM_THREE', name: 'PLO ပုံစံ (၃)' },
  { id: 'PLO_FORM_FOUR', name: 'PLO ပုံစံ (၄)' },
  { id: 'PLO_FORM_FIVE', name: 'PLO ပုံစံ (၅)' },
  { id: 'PLO_FORM_SIX', name: 'PLO ပုံစံ (၆)' },
  { id: 'LEGAL_ADVICE_FORM_ONE', name: 'ဥပရ အမှုလိုက် ပုံစံ (၁)' },
  { id: 'LEGAL_ADVICE_FORM_TWO', name: 'ဥပရ အမှုလိုက် ပုံစံ (၂)' },
  { id: 'LEGAL_ADVICE_FORM_THREE', name: 'ဥပရ အမှုလိုက် ပုံစံ (၃)' },
  { id: 'LEGAL_ADVICE_FORM_FOUR', name: 'ဥပရ အမှုလိုက် ပုံစံ (၄)' },
  { id: 'LEGAL_ADVICE_FORM_FIVE', name: 'ဥပရ အမှုလိုက် ပုံစံ (၅)' },
  { id: 'LEGAL_ADVICE_FORM_SIX', name: 'ဥပရ အမှုလိုက် ပုံစံ (၆)' },
  { id: 'LEGAL_ADVICE_FORM_SEVEN', name: 'ဥပရ အမှုလိုက် ပုံစံ (၇)' },
  { id: 'LEGAL_ADVICE_FORM_SEVEN_A', name: 'ဥပရ အမှုလိုက် ပုံစံ (၇ က)' },
  { id: 'LEGAL_ADVICE_FORM_EIGHT', name: 'ဥပရ အမှုလိုက် ပုံစံ (၈)' },
  { id: 'LEGAL_ADVICE_FORM_EIGHT_A', name: 'ဥပရ အမှုလိုက် ပုံစံ (၈ က)' },
  { id: 'LEGAL_ADVICE_FORM_NINE', name: 'ဥပရ အမှုလိုက် ပုံစံ (၉)' },
  { id: 'LEGAL_ADVICE_FORM_TEN', name: 'ဥပရ အမှုလိုက် ပုံစံ (၁၀)' },
  { id: 'LEGAL_ADVICE_FORM_ELEVEN', name: 'ဥပရ အမှုလိုက် ပုံစံ (၁၁)' },
];

const ITEMS_PER_PAGE = 5;

const FORM_TYPE_MAP: Record<string, string> = {
  PLO_FORM_ONE: 'PLO ပုံစံ (၁)',
  PLO_FORM_TWO: 'PLO ပုံစံ (၂)',
  PLO_FORM_THREE: 'PLO ပုံစံ (၃)',
  PLO_FORM_FOUR: 'PLO ပုံစံ (၄)',
  PLO_FORM_FIVE: 'PLO ပုံစံ (၅)',
  PLO_FORM_SIX: 'PLO ပုံစံ (၆)',
  LEGAL_ADVICE_FORM_ONE: 'ဥပရ အမှုလိုက် ပုံစံ (၁)',
  LEGAL_ADVICE_FORM_TWO: 'ဥပရ အမှုလိုက် ပုံစံ (၂)',
  LEGAL_ADVICE_FORM_THREE: 'ဥပရ အမှုလိုက် ပုံစံ (၃)',
  LEGAL_ADVICE_FORM_FOUR: 'ဥပရ အမှုလိုက် ပုံစံ (၄)',
  LEGAL_ADVICE_FORM_FIVE: 'ဥပရ အမှုလိုက် ပုံစံ (၅)',
  LEGAL_ADVICE_FORM_SIX: 'ဥပရ အမှုလိုက် ပုံစံ (၆)',
  LEGAL_ADVICE_FORM_SEVEN: 'ဥပရ အမှုလိုက် ပုံစံ (၇)',
  LEGAL_ADVICE_FORM_EIGHT: 'ဥပရ အမှုလိုက် ပုံစံ (၈)',
  LEGAL_ADVICE_FORM_SEVEN_A: 'ဥပရ အမှုလိုက် ပုံစံ (၇ က)',
  LEGAL_ADVICE_FORM_EIGHT_A: 'ဥပရ အမှုလိုက် ပုံစံ (၈ က)',
  LEGAL_ADVICE_FORM_NINE: 'ဥပရ အမှုလိုက် ပုံစံ (၉)',
  LEGAL_ADVICE_FORM_TEN: 'ဥပရ အမှုလိုက် ပုံစံ (၁၀)',
  LEGAL_ADVICE_FORM_ELEVEN: 'ဥပရ အမှုလိုက် ပုံစံ (၁၁)',
};

const FORM_ROUTE_MAP: Record<string, { edit: string; detail?: string }> = {
  PLO_FORM_ONE: {
    edit: '/plo-form-one/edit',
    detail: '/plo-form-one-detail',
  },
  PLO_FORM_TWO: {
    edit: '/plo-form-two/edit',
    detail: '/plo-form-two-detail',
  },
  PLO_FORM_THREE: {
    edit: '/plo-form-three/edit',
  },
  PLO_FORM_FOUR: {
    edit: '/plo-form-four/edit',
  },
  PLO_FORM_FIVE: {
    edit: '/plo-form-five/edit',
  },
  PLO_FORM_SIX: {
    edit: '/plo-form-Six/edit',
  },
  LEGAL_ADVICE_FORM_ONE: {
    edit: '/legal-form-one/edit',
    detail: '/legal-form-one-detail',
  },
  LEGAL_ADVICE_FORM_TWO: { edit: '/legal-form-two/edit' },
  LEGAL_ADVICE_FORM_THREE: {
    edit: '/legal-form-three/edit',
    detail: '/legal-form-three-detail',
  },
  LEGAL_ADVICE_FORM_FOUR: { edit: '/legal-form-four/edit' },
  LEGAL_ADVICE_FORM_FIVE: { edit: '/legal-form-five/edit' },
  LEGAL_ADVICE_FORM_SIX: { edit: '/legal-form-six/edit' },
  LEGAL_ADVICE_FORM_SEVEN: { edit: '/legal-form-seven/edit' },
  LEGAL_ADVICE_FORM_EIGHT: { edit: '/legal-form-eight/edit' },
  LEGAL_ADVICE_FORM_SEVEN_A: { edit: '/legal-form-seven-a/edit' },
  LEGAL_ADVICE_FORM_EIGHT_A: { edit: '/legal-form-eight-a/edit' },
  LEGAL_ADVICE_FORM_NINE: { edit: '/legal-form-nine/edit' },
  LEGAL_ADVICE_FORM_TEN: { edit: '/legal-form-ten/edit' },
  LEGAL_ADVICE_FORM_ELEVEN: { edit: '/legal-form-eleven/edit' },
};

const FORM_DELETE_ENDPOINT_MAP: Record<string, string> = {
  PLO_FORM_ONE: 'plo-form-one',
  PLO_FORM_TWO: 'plo-form-two',
  PLO_FORM_THREE: 'plo-form-three',
  PLO_FORM_FOUR: 'plo-form-four',
  PLO_FORM_FIVE: 'plo-form-five',
  PLO_FORM_SIX: 'plo-form-six',
  LEGAL_ADVICE_FORM_ONE: 'legal-advice-form-one',
  LEGAL_ADVICE_FORM_TWO: 'legal-advice-form-two',
  LEGAL_ADVICE_FORM_THREE: 'legal-advice-form-three',
  LEGAL_ADVICE_FORM_FOUR: 'legal-advice-form-four',
  LEGAL_ADVICE_FORM_FIVE: 'legal-advice-form-five',
  LEGAL_ADVICE_FORM_SIX: 'legal-advice-form-six',
  LEGAL_ADVICE_FORM_SEVEN: 'legal-advice-form-seven',
  LEGAL_ADVICE_FORM_EIGHT: 'legal-advice-form-eight',
  LEGAL_ADVICE_FORM_SEVEN_A: 'legal-advice-form-seven-a',
  LEGAL_ADVICE_FORM_EIGHT_A: 'legal-advice-form-eight-a',
  LEGAL_ADVICE_FORM_NINE: 'legal-advice-form-nine',
  LEGAL_ADVICE_FORM_TEN: 'legal-advice-form-ten',
  LEGAL_ADVICE_FORM_ELEVEN: 'legal-advice-form-eleven',
};

const INITIAL_SEARCH_PARAMS = {
  fromDate: '',
  toDate: '',
  documentNumber: '',
  officeId: '',
  status: '' as SubmissionStatus | '',
  personName: '',
  lawSection: '',
  formType: '',
};

type SearchPageCache = {
  globalQuery: string;
  searchParams: typeof INITIAL_SEARCH_PARAMS;
  currentPage: number;
  results: DocumentSearchResult[];
  totalItems: number;
};

let searchPageCache: SearchPageCache | null = null;
let searchPageHasLoaded = false;
let searchPageRequestInFlight = false;

export default function SearchPage() {
  const navigate = useNavigate();
  const { settings } = useSettings();

  const restoredCache = searchPageCache;

  // const DISTRICT_OPTIONS = useMemo(() => settings?.districts || [], [settings]);
  const OFFICE_OPTIONS = useMemo(() => settings?.offices || [], [settings]);

  // UI State Management
  const [globalQuery, setGlobalQuery] = useState(restoredCache?.globalQuery || '');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(restoredCache?.currentPage || 1);

  // API Data State
  const [results, setResults] = useState<DocumentSearchResult[]>(restoredCache?.results || []);
  const [totalItems, setTotalItems] = useState(restoredCache?.totalItems || 0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form Filter Parameters
  const [searchParams, setSearchParams] = useState(
    restoredCache?.searchParams || INITIAL_SEARCH_PARAMS
  );

  // Centralized Parameterized API Fetching Logic
  const loadCases = useCallback(
    async (page: number, query: string, params: typeof INITIAL_SEARCH_PARAMS) => {
      searchPageRequestInFlight = true;
      setIsLoading(true);
      setError(null);

      try {
        const response = await searchDocuments({
          searchTerm: query.trim() ? toBurmeseDigits(query.trim()) : undefined,
          fromDate: params.fromDate || undefined,
          toDate: params.toDate || undefined,
          documentNumber: params.documentNumber.trim() ? toBurmeseDigits(params.documentNumber.trim()) : undefined,
          officeId: params.officeId || undefined,
          status: params.status || undefined,
          personName: params.personName.trim() || undefined,
          lawSection: params.lawSection.trim() ? toBurmeseDigits(params.lawSection.trim()) : undefined,
          formType: params.formType || undefined,
          currentPage: page,
          itemsPerPage: ITEMS_PER_PAGE,
        });

        setResults(response.data);
        setTotalItems(response.totalItems);
        searchPageHasLoaded = true;
        searchPageCache = {
          globalQuery: query,
          searchParams: params,
          currentPage: page,
          results: response.data,
          totalItems: response.totalItems,
        };
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'အချက်အလက်များ ရယူရာတွင် အမှားတစ်ခု ဖြစ်ပေါ်နေပါသည်။';
        setError(errorMessage);
        setResults([]);
        setTotalItems(0);
      } finally {
        searchPageRequestInFlight = false;
        setIsLoading(false);
      }
    },
    []
  );

  // Pagination Change Trigger
  useEffect(() => {
    if (
      (restoredCache && currentPage === restoredCache.currentPage) ||
      searchPageRequestInFlight
    ) {
      return;
    }

    loadCases(currentPage, globalQuery, searchParams);
  }, [currentPage, loadCases]);

  useEffect(() => {
    if (!searchPageHasLoaded) return;

    searchPageCache = {
      globalQuery,
      searchParams,
      currentPage,
      results,
      totalItems,
    };
  }, [globalQuery, searchParams, currentPage, results, totalItems]);

  // Event Handlers
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setSearchParams((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearchExecution = (e: React.FormEvent) => {
    e.preventDefault();
    if (currentPage === 1) {
      loadCases(1, globalQuery, searchParams);
    } else {
      setCurrentPage(1);
    }
  };

  const handleClearFilters = () => {
    setGlobalQuery('');
    setSearchParams(INITIAL_SEARCH_PARAMS);
    
    if (currentPage === 1) {
      loadCases(1, '', INITIAL_SEARCH_PARAMS);
    } else {
      setCurrentPage(1);
    }
  };

  const handleViewDetail = (caseId?: string, status?: string, formType?: string) => {
    if (!caseId) return;

    const routes = (formType && FORM_ROUTE_MAP[formType]) || FORM_ROUTE_MAP.PLO_FORM_ONE;

    if (status === 'DRAFT') {
      navigate(`${routes.edit}/${caseId}`);
    } else if (routes.detail) {
      navigate(`${routes.detail}/${caseId}`);
    } else {
      navigate(`${routes.edit}/${caseId}`);
    }
  };

  const handleEdit = (caseId?: string, formType?: string) => {
    if (!caseId) return;

    const routes = (formType && FORM_ROUTE_MAP[formType]) || FORM_ROUTE_MAP.PLO_FORM_ONE;
    navigate(`${routes.edit}/${caseId}`);
  };

  const handleDelete = async (id?: string, formType?: string) => {
    if (!id || !formType) return;

    const confirmed = window.confirm('ဤမှတ်တမ်းကို ဖျက်ရန် သေချာပါသလား။');
    if (!confirmed) return;

    try {
      const endpoint = FORM_DELETE_ENDPOINT_MAP[formType];
      if (!endpoint) {
        throw new Error('ဤပုံစံကို ဖျက်ရန် မထောက်ပံ့သေးပါ။');
      }

      await deleteForm(endpoint, id);
      await loadCases(currentPage, globalQuery, searchParams);
    } catch (err: any) {
      console.error('Delete failed:', err);
      alert(err?.message || 'ဖျက်ခြင်း မအောင်မြင်ပါ။');
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));
  const hasActiveFilters =
    globalQuery !== '' ||
    Object.values(searchParams).some((value) => value !== '');

  // Dynamic Pagination Window Generator (handles large page counts cleanly)
  const getPaginationPages = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = start; i <= end; i++) pages.push(i);
      
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const [currentUser] = useState<User | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
      return null;
    }
  });

  const checkIsOriginUser = (app: any): boolean => {
    if (!currentUser) return false;

    const source = app.raw || app;

    // 1. Match by Office ID (Top-level originOfficeId or nested originOffice.id)
    const originOfficeId = source.originOfficeId || source.originOffice?.id || source.document?.originOfficeId;
    if (currentUser.officeId && originOfficeId && currentUser.officeId === originOfficeId) {
      return true;
    }

    // 2. Match by Creator User ID
    const createdById = source.createdById || source.createdBy?.id;
    if (currentUser.id && createdById && currentUser.id === createdById) {
      return true;
    }

    return false;
  };

  return (
    <div className="space-y-3 p-6 max-w-7xl mx-auto">
      {/* 1. SEARCH HEADER BAR */}
      <div className="bg-white rounded-xl border border-slate-200 p-2.5 shadow-sm">
        <form onSubmit={handleSearchExecution} className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              value={globalQuery}
              onChange={(e) => setGlobalQuery(e.target.value)}
              placeholder="ရှာဖွေလိုသည့် အမှုတွဲအမှတ်၊ အမည်၊ ဥပဒေပုဒ်မ (သို့) အကြောင်းအရာဖြင့် ရှာဖွေပါ..."
              className="w-full pl-10 pr-4 py-2.5 text-sm rounded-lg border border-slate-200 bg-slate-50/50 text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all"
            />
          </div>

          <div className="flex items-center gap-2 shrink-0 justify-end">
            <button
              type="button"
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className={`inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-xs font-semibold border transition-all ${
                showAdvancedFilters 
                  ? 'bg-blue-50 text-blue-700 border-blue-200 shadow-sm' 
                  : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
              }`}
            >
              <SlidersHorizontal className="h-3.5 w-3.5" />
              <span>အဆင့်မြင့်စစ်ထုတ်မှု</span>
              {showAdvancedFilters && <span className="h-2 w-2 rounded-full bg-blue-600 animate-pulse" />}
            </button>

            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-5 py-2.5 text-xs font-bold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:opacity-70 transition-colors"
            >
              {isLoading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Search className="h-3.5 w-3.5" />
              )}
              {isLoading ? 'ရှာဖွေနေပါသည်...' : 'ရှာဖွေရန်'}
            </button>
          </div>
        </form>
      </div>

      {/* 2. ADVANCED FILTERS WRAPPER */}
      {showAdvancedFilters && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm transition-all animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="mb-5 flex items-center gap-2 border-b border-slate-100 pb-3 text-sm font-semibold text-slate-800">
            <Filter className="h-4 w-4 text-blue-600" />
            <span>ရှာဖွေလိုသည့် အချက်အလက်များ သတ်မှတ်ရန်</span>
          </div>

          <form onSubmit={handleSearchExecution} className="space-y-6">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {/* ၁။ စတင်ရက် - ဆုံးရက် */}
              <div className="space-y-1.5 lg:col-span-1">
                <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5 text-slate-400" />
                  ၁။ စတင်ရက် - ဆုံးရက် (နှစ်ရက်ကြား)
                </label>
                <div className="flex items-center gap-2">
                  <FormDatePicker
                    name="fromDate"
                    value={searchParams.fromDate || ''}
                    useMyanmarDateFormat={false}
                    onChange={handleInputChange}
                    className="min-w-0 flex-1"
                  />
                  <span className="text-xs font-medium text-slate-400">မှ</span>
                  <FormDatePicker
                    name="toDate"
                    value={searchParams.toDate || ''}
                    useMyanmarDateFormat={false}
                    onChange={handleInputChange}
                    className="min-w-0 flex-1"
                  />
                </div>
              </div>

              {/* ၂။ စာအမှတ် */}
              <div className="space-y-1.5 lg:col-span-1">
                <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                  <FileText className="h-3.5 w-3.5 text-slate-400" />
                  ၂။ စာအမှတ်
                </label>
                <input
                  type="text"
                  name="documentNumber"
                  placeholder="ဥပမာ- LA3-2026-00001 (သို့) စာအမှတ်"
                  value={searchParams.documentNumber || ''}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none"
                />
              </div>

              {/* ၃။ ရုံး */}
              <div className="space-y-1.5 lg:col-span-1">
                <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5 text-slate-400" />
                  ၃။ ရုံး
                </label>
                <select
                  name="officeId"
                  value={searchParams.officeId || ''}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 focus:border-blue-500 focus:outline-none"
                >
                  <option value="">ရုံး အားလုံး</option>
                  {OFFICE_OPTIONS.map((office) => (
                    <option key={office.id} value={office.id}>
                      {office.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* ၄။ ပြီးစီးမှု အခြေအနေ */}
              <div className="space-y-1.5 lg:col-span-1">
                <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                  <Layers className="h-3.5 w-3.5 text-slate-400" />
                  ၄။ ပြီးစီးမှု အခြေအနေ
                </label>
                <select
                  name="status"
                  value={searchParams.status || ''}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 focus:border-blue-500 focus:outline-none"
                >
                <option value="">အခြေအနေ အားလုံး</option>
                <option value="DRAFT">မူကြမ်း</option>
                <option value="APPROVED">အတည်ပြုပြီး</option>
                <option value="SUBMITTED">တင်ပြပြီး</option>
                <option value="RECEIVED">လက်ခံရရှိသည်</option>
                <option value="UNDER_REVIEW">ကြည့်ရှုထားသည် </option>
                <option value="ATTACHED">ညွန်ကြားထားသည် </option>
                </select>
              </div>

              {/* ၅။ တရားလို / တရားခံ အမည် */}
              <div className="space-y-1.5 lg:col-span-1">
                <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                  <UserCheck className="h-3.5 w-3.5 text-slate-400" />
                  ၅။ တရားလို / တရားခံ အမည်
                </label>
                <input
                  type="text"
                  name="personName"
                  placeholder="အမည်ဖြင့် ရှာဖွေရန်"
                  value={searchParams.personName || ''}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none"
                />
              </div>

              {/* ၆။ ဥပဒေပုဒ်မများ */}
              <div className="space-y-1.5 lg:col-span-1">
                <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                  <FileText className="h-3.5 w-3.5 text-slate-400" />
                  ၆။ ဥပဒေပုဒ်မများ
                </label>
                <input
                  type="text"
                  name="lawSection"
                  placeholder="ဥပမာ- ၃၀၂၊ ၃၂၆၊ ၃၇၉"
                  value={searchParams.lawSection || ''}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none"
                />
              </div>

              {/* ၇။ ဥပဒေအမှုလိုက်ပုံစံ */}
              <div className="space-y-1.5 lg:col-span-1">
                <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                  <FileText className="h-3.5 w-3.5 text-slate-400" />
                  ၇။ ပုံစံများ
                </label>
                <select
                  name="formType"
                  value={searchParams.formType || ''}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 focus:border-blue-500 focus:outline-none"
                >
                  <option value="">ဥပရအမှုလိုက်ပုံစံ အားလုံး</option>
                  {FORM_TYPES.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-end gap-3 border-t border-slate-100 pt-4">
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  စစ်ထုတ်မှုများ ဖျက်ရန်
                </button>
              )}
              
              <button
                type="submit"
                disabled={isLoading}
                className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-5 py-2 text-xs font-bold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:opacity-70 transition-colors"
              >
                {isLoading ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Search className="h-3.5 w-3.5" />
                )}
                {isLoading ? 'ရှာဖွေနေပါသည်...' : 'ရှာဖွေရန်'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* 3. RESULT DISPLAY SECTION */}
      <div className="mt-2">
        {error && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4 text-xs font-semibold text-red-600">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="flex flex-col items-center justify-center rounded-xl border border-slate-200 bg-white p-12 text-center shadow-sm">
            <Loader2 className="h-8 w-8 text-blue-600 animate-spin mb-3" />
            <p className="text-xs font-medium text-slate-600">အချက်အလက်များကို ရယူနေပါသည်...</p>
          </div>
        ) : results.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50/70 p-12 text-center">
            <FolderOpen className="h-10 w-10 text-slate-300 mb-3 stroke-[1.5]" />
            <p className="text-sm font-medium text-slate-500">ရလဒ်များ မရှိသေးပါ</p>
            <p className="text-xs text-slate-400 mt-1">အချက်အလက်များကို ပြန်လည်စစ်ဆေးပြီး ထပ်မံရှာဖွေကြည့်ပါ။</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
              <h3 className="text-xs font-bold text-slate-700">ရှာဖွေတွေ့ရှိသော စာရင်းများ ({toBurmeseDigits(totalItems)} စောင်)</h3>
            </div>
            
            <div className="divide-y divide-slate-100">
              {results.map((item) => {
                const isOriginUser = checkIsOriginUser(item);
                return <div key={item.id} className="p-4 hover:bg-slate-50/50 transition-colors flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="text-xs font-bold px-2 py-0.5 bg-blue-50 text-blue-700 rounded border border-blue-100">
                        {item.documentNumber}
                      </span>
                      {item.formType && (
                        <span className="text-[11px] font-medium px-2 py-0.5 bg-slate-100 text-slate-600 rounded">
                          {FORM_TYPE_MAP[item.formType] || item.formType}
                        </span>
                      )}
                      <span className="text-[11px] text-slate-400">
                        {item.createdAt ? formatDateDisplay(item.createdAt) : 'N/A'}
                      </span>
                    </div>
                    
                    <div className="flex flex-wrap items-center text-xs text-slate-500 gap-x-3 gap-y-1 mt-2">
                      <span>တရားလို: <strong className="text-slate-700 font-medium">{item.complainantOrPlaintiff || '-'}</strong></span>
                      <span className="text-slate-300 hidden sm:inline">•</span>
                      <span>တရားခံ: <strong className="text-slate-700 font-medium">{item.accusedOrDefendant || '-'}</strong></span>
                      <span className="text-slate-300 hidden sm:inline">•</span>
                      <span>သက်ဆိုင်ရာပုဒ်မ: <strong className="text-blue-600 font-medium">{item.lawSection || '-'}</strong></span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between md:justify-end gap-3 shrink-0 pt-3 md:pt-0 border-t border-slate-50 md:border-none">
                    <div className="flex items-center gap-2">
                      {item.originOfficeName && (
                        <span className="text-[11px] font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded">
                          {item.originOfficeName}
                        </span>
                      )}
                      {/* <span className={`text-[11px] font-bold px-2 py-1 rounded ${
                        item.status === 'APPROVED' ? 'bg-green-50 text-green-700' :
                        item.status === 'UNDER_REVIEW' || item.status === 'SUBMITTED' ? 'bg-amber-50 text-amber-700' 
                        :
                        item.status === 'ATTACHED' ? 'bg-red-50 text-red-700' : 'bg-slate-100 text-slate-600'
                      }`}>
                        {item.status}
                      </span> */}
                     <StatusBadge status={item.status} isOriginUser={isOriginUser} />
                    </div>

                    {item.status === 'DRAFT' ? (
                            <>
                              <button
                                type="button"
                                onClick={() => handleEdit(item.documentNumber, item.formType)}
                                className="inline-flex items-center gap-1 rounded bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-600 hover:bg-blue-100 transition-colors"
                                title="ပြင်ဆင်ရန်"
                              >
                                ✏️ ပြင်ဆင်ရန်
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDelete(item.documentNumber, item.formType)}
                                className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition-colors"
                                title="ဖျက်ရန်"
                              >
                                🗑️
                              </button>
                            </>
                          ) : item.status === 'APPROVED' ? (
                            <button
                              type="button"
                              onClick={() => handleEdit(item.documentNumber, item.formType)}
                              className="inline-flex items-center gap-1 rounded bg-blue-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-blue-700 transition-colors shadow-sm"
                              title="ပေးပို့ရန်"
                            >
                              🚀 တင်ပြရန်
                            </button>
                          ) : item.status === 'SUBMITTED' && isOriginUser ? (
                            <button
                              type="button"
                              onClick={() => handleEdit(item.documentNumber, item.formType)}
                              className="inline-flex items-center gap-1 rounded bg-blue-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-blue-700 transition-colors shadow-sm"
                              title="ပေးပို့ရန်"
                            >
                              🚀 တင်ပြရန်
                            </button>
                          ): item.status === 'UNDER_REVIEW' && isOriginUser ? (
                            <button
                              type="button"
                              onClick={() => handleEdit(item.documentNumber, item.formType)}
                              className="inline-flex items-center gap-1 rounded bg-blue-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-blue-700 transition-colors shadow-sm"
                              title="ပေးပို့ရန်"
                            >
                              🚀 တင်ပြရန်
                            </button>
                          ): (
                            <button
                              type="button"
                              onClick={() => handleViewDetail(item.documentNumber, item.status, item.formType)}
                              className="rounded px-2.5 py-1 text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 transition-colors shadow-sm"
                            >
                              🔍 စိစစ်ရန်
                            </button>
                          )}
                  </div>
                </div>
              })}
            </div>

            {/* PAGINATION CONTROLS */}
            {totalPages > 1 && (
              <div className="px-5 py-4 border-t border-slate-100 bg-slate-50/30 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-xs text-slate-500 font-medium">
                  စုစုပေါင်း <span className="text-slate-800 font-bold">{toBurmeseDigits(totalItems)}</span> စောင်အနက်မှ ပုံစံ 
                  <span className="text-slate-800 font-bold mx-1">
                    {toBurmeseDigits(((currentPage - 1) * ITEMS_PER_PAGE) + 1)} - {toBurmeseDigits(Math.min(currentPage * ITEMS_PER_PAGE, totalItems))}
                  </span> 
                  ကို ပြသနေသည်။
                </div>
                
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    disabled={currentPage === 1 || isLoading}
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    className="p-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:hover:bg-white transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>

                  {getPaginationPages().map((page, idx) => 
                    typeof page === 'number' ? (
                      <button
                        key={page}
                        type="button"
                        disabled={isLoading}
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${
                          currentPage === page
                            ? 'bg-blue-600 text-white shadow-sm'
                            : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        {toBurmeseDigits(page)}
                      </button>
                    ) : (
                      <span key={`ellipse-${idx}`} className="px-1 text-xs text-slate-400">
                        {page}
                      </span>
                    )
                  )}

                  <button
                    type="button"
                    disabled={currentPage === totalPages || isLoading}
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    className="p-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:hover:bg-white transition-colors"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}